// src/components/TripPlayback.jsx
// Photos-on-map trip playback — camera approaches photos placed on the map
import { useState, useEffect, useRef, useCallback } from "react";

const FL = "'Manrope', -apple-system, sans-serif";
const FH = "'Noto Serif', Georgia, serif";
const TRIP_COLOR = "#E8943A";

const ZOOM_CLOSE = 18;
const AUTO_INTERVAL = 3000;

const PHOTO_SMALL = 56;

export default function TripPlayback({ trip, map, onClose, leftOffset = 0, onSignIn }) {
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [arrived, setArrived] = useState(false);
  const [fadeIn, setFadeIn] = useState(false);
  const autoRef = useRef(null);
  const prevIdxRef = useRef(-1);
  const overlaysRef = useRef([]);
  const goToStopRef = useRef(null);
  const handlePrevRef = useRef(null);
  const handleNextRef = useRef(null);
  const stops = trip?.stops || [];
  const isDesktop = leftOffset > 0;

  const [preloading, setPreloading] = useState(true);

  const PHOTO_ACTIVE_W = isDesktop ? 340 : Math.min(286, window.innerWidth * 0.72);

  // Pre-load tiles at ZOOM_CLOSE for each stop before starting animation
  useEffect(() => {
    if (!map || stops.length === 0) return;
    let cancelled = false;

    const waitForIdle = () => new Promise((resolve) => {
      const l = map.addListener("idle", () => { l.remove(); resolve(); });
      setTimeout(() => { l.remove(); resolve(); }, 1500);
    });

    (async () => {
      for (const stop of stops) {
        if (cancelled) return;
        map.moveCamera({ center: { lat: stop.lat, lng: stop.lng }, zoom: ZOOM_CLOSE });
        await waitForIdle();
        for (const z of [ZOOM_CLOSE - 2, ZOOM_CLOSE - 4]) {
          if (cancelled) return;
          map.moveCamera({ center: { lat: stop.lat, lng: stop.lng }, zoom: z });
          await waitForIdle();
        }
      }
      if (cancelled) return;

      const bounds = new window.google.maps.LatLngBounds();
      stops.forEach((s) => bounds.extend({ lat: s.lat, lng: s.lng }));
      map.fitBounds(bounds, 60);
      await waitForIdle();

      // Preload all stop photos so they're cached by the browser
      if (!cancelled) {
        const preloadPromises = stops.map((s) => {
          const src = s.photos?.[0]?.blobUrl || s.photos?.[0]?.url || s.thumbnailUrl;
          if (!src) return Promise.resolve();
          return new Promise((resolve) => {
            const preImg = new Image();
            preImg.onload = resolve;
            preImg.onerror = resolve;
            preImg.src = src;
          });
        });
        await Promise.all(preloadPromises);
      }

      if (cancelled) return;
      setPreloading(false);
      setFadeIn(true);
      setTimeout(() => {
        if (goToStopRef.current) goToStopRef.current(0);
      }, 300);
    })();

    return () => { cancelled = true; };
  }, []); // eslint-disable-line

  // Create photo overlays on the map
  useEffect(() => {
    if (!map || stops.length === 0 || preloading) return;

    // Clean up previous
    overlaysRef.current.forEach((o) => { if (o.marker) o.marker.map = null; });

    const overlays = stops.map((stop, i) => {
      const el = document.createElement("div");
      el.style.cssText = "display:flex;flex-direction:column;align-items:center;cursor:pointer;";

      const frame = document.createElement("div");
      frame.style.cssText = `
        width:${PHOTO_SMALL}px;height:${PHOTO_SMALL}px;
        border-radius:6px;overflow:hidden;
        border:2px solid rgba(255,255,255,0.9);
        box-shadow:0 2px 8px rgba(0,0,0,0.15);
        position:relative;
        transition: width 0.6s cubic-bezier(0.16,1,0.3,1),
                    height 0.6s cubic-bezier(0.16,1,0.3,1),
                    border-radius 0.4s ease,
                    box-shadow 0.4s ease;
      `;

      const img = document.createElement("img");
      const firstPhoto = stop.photos?.[0];
      const photoSrc = firstPhoto?.blobUrl || firstPhoto?.url || stop.thumbnailUrl || "";
      img.style.cssText = "width:100%;height:100%;object-fit:cover;display:block;background:#ede0d5;";
      img.draggable = false;
      if (photoSrc) img.src = photoSrc;
      img.addEventListener("load", () => {
        // When active, auto-update frame height to match photo's natural aspect ratio
        if (parseInt(frame.style.width) > PHOTO_SMALL && img.naturalWidth > 0) {
          const w = parseInt(frame.style.width);
          const maxH = isDesktop ? 350 : Math.min(300, window.innerHeight * 0.4);
          const h = Math.round(w * (img.naturalHeight / img.naturalWidth));
          frame.style.height = Math.max(w * 0.55, Math.min(maxH, h)) + "px";
        }
      });
      frame.appendChild(img);

      if (!firstPhoto?.blobUrl && !firstPhoto?.url && !stop.thumbnailUrl) {
        const numDiv = document.createElement("div");
        numDiv.style.cssText = `
          position:absolute;inset:0;
          display:flex;align-items:center;justify-content:center;
          background:#ede0d5;color:${TRIP_COLOR};
          font-family:Manrope,-apple-system,sans-serif;font-size:18px;font-weight:800;
        `;
        numDiv.textContent = i + 1;
        frame.appendChild(numDiv);
      }

      // Badge
      const badge = document.createElement("div");
      badge.style.cssText = `
        position:absolute;top:6px;right:6px;
        background:rgba(0,0,0,0.5);color:#fff;
        padding:2px 6px;border-radius:4px;
        font-family:Manrope,sans-serif;font-size:10px;font-weight:600;
        backdrop-filter:blur(4px);
        opacity:0;transition:opacity 0.3s;
        pointer-events:none;
      `;
      const photoCount = stop.photos?.length || 0;
      badge.textContent = photoCount > 1 ? `1/${photoCount}` : "";
      frame.appendChild(badge);

      // Uploader avatar
      if (stop.uploaded_by_nickname) {
        const avatar = document.createElement("div");
        if (stop.uploaded_by_photo) {
          avatar.style.cssText = `
            position:absolute;bottom:6px;left:6px;
            width:22px;height:22px;border-radius:50%;
            background:url(${stop.uploaded_by_photo}) center/cover;
            border:2px solid rgba(255,255,255,0.9);
            box-shadow:0 1px 4px rgba(0,0,0,0.2);
            opacity:0;transition:opacity 0.3s;pointer-events:none;
          `;
        } else {
          avatar.style.cssText = `
            position:absolute;bottom:6px;left:6px;
            width:22px;height:22px;border-radius:50%;
            background:linear-gradient(135deg,#595149,#655d54);
            border:2px solid rgba(255,255,255,0.9);
            box-shadow:0 1px 4px rgba(0,0,0,0.2);
            display:flex;align-items:center;justify-content:center;
            font-family:Manrope,sans-serif;font-size:9px;color:#fff6ef;font-weight:700;
            opacity:0;transition:opacity 0.3s;pointer-events:none;
          `;
          avatar.textContent = (stop.uploaded_by_nickname[0] || "?").toUpperCase();
        }
        avatar.dataset.uploaderAvatar = "1";
        frame.appendChild(avatar);
      }

      // Dots for multiple photos
      const dots = document.createElement("div");
      dots.style.cssText = `
        position:absolute;bottom:8px;left:50%;
        transform:translateX(-50%);
        display:flex;gap:4px;
        opacity:0;transition:opacity 0.3s;
        pointer-events:none;
      `;
      if (photoCount > 1) {
        for (let j = 0; j < Math.min(photoCount, 8); j++) {
          const dot = document.createElement("span");
          dot.style.cssText = `
            width:5px;height:5px;border-radius:50%;
            background:rgba(255,255,255,${j === 0 ? "1" : "0.5"});
            transition:all 0.3s;
          `;
          dot.dataset.dotIndex = j;
          dots.appendChild(dot);
        }
      }
      frame.appendChild(dots);

      // Swipe gesture on photo
      let touchStartX = 0, touchStartY = 0, swiping = false;
      frame.addEventListener("touchstart", (e) => {
        if (parseInt(frame.style.width) <= PHOTO_SMALL) return;
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
        swiping = false;
      }, { passive: true });
      frame.addEventListener("touchmove", (e) => {
        if (parseInt(frame.style.width) <= PHOTO_SMALL) return;
        const dx = e.touches[0].clientX - touchStartX;
        const dy = Math.abs(e.touches[0].clientY - touchStartY);
        if (Math.abs(dx) > dy && Math.abs(dx) > 10) {
          swiping = true;
          e.stopPropagation();
          img.style.transform = `translateX(${dx * 0.3}px)`;
        }
      }, { passive: true });
      frame.addEventListener("touchend", () => {
        if (!swiping) return;
        const dx = parseFloat(img.style.transform.replace(/[^-\d.]/g, "")) || 0;
        img.style.transition = "transform 0.2s ease";
        img.style.transform = "translateX(0)";
        setTimeout(() => { img.style.transition = ""; }, 200);
        if (Math.abs(dx) > 12) {
          if (dx < 0 && handleNextRef.current) handleNextRef.current();
          else if (dx > 0 && handlePrevRef.current) handlePrevRef.current();
        }
        swiping = false;
      });

      el.appendChild(frame);

      // Label
      const label = document.createElement("div");
      label.style.cssText = `
        margin-top:6px;white-space:nowrap;
        font-family:Manrope,-apple-system,sans-serif;
        font-size:11px;font-weight:700;
        color:#2f3430;background:rgba(255,255,255,0.9);
        padding:3px 8px;border-radius:4px;
        box-shadow:0 1px 4px rgba(0,0,0,0.1);
        opacity:0;transition:opacity 0.3s;
        pointer-events:none;max-width:160px;
        overflow:hidden;text-overflow:ellipsis;
      `;
      label.textContent = stop.name || `Stop ${i + 1}`;
      el.appendChild(label);

      const marker = new window.google.maps.marker.AdvancedMarkerElement({
        position: { lat: stop.lat, lng: stop.lng },
        map,
        content: el,
        zIndex: 1,
      });

      marker.addListener("click", () => {
        if (goToStopRef.current) goToStopRef.current(i);
      });

      return { marker, el, frame, img, badge, label, dots };
    });

    overlaysRef.current = overlays;

    return () => {
      overlays.forEach((o) => { if (o.marker) o.marker.map = null; });
    };
  }, [map, stops.length, preloading]); // eslint-disable-line

  // Update photo overlay active states
  useEffect(() => {
    const overlays = overlaysRef.current;
    if (!overlays.length) return;

    overlays.forEach((o, i) => {
      const isActive = i === currentIdx && arrived;

      if (isActive) {
        // Dynamic height from photo's natural aspect ratio
        const maxH = isDesktop ? 350 : Math.min(300, window.innerHeight * 0.4);
        let h = Math.round(PHOTO_ACTIVE_W * 1.25); // default 4:5 fallback
        if (o.img.naturalWidth > 0 && o.img.naturalHeight > 0) {
          h = Math.round(PHOTO_ACTIVE_W * (o.img.naturalHeight / o.img.naturalWidth));
        }
        h = Math.max(Math.round(PHOTO_ACTIVE_W * 0.55), Math.min(maxH, h));

        o.frame.style.width = PHOTO_ACTIVE_W + "px";
        o.frame.style.height = h + "px";
        o.frame.style.borderRadius = "12px";
        o.frame.style.borderWidth = "3px";
        o.frame.style.boxShadow = "0 8px 32px rgba(0,0,0,0.3), 0 2px 8px rgba(0,0,0,0.15)";
        o.badge.style.opacity = (stops[i]?.photos?.length || 0) > 1 ? "1" : "0";
        o.dots.style.opacity = (stops[i]?.photos?.length || 0) > 1 ? "1" : "0";
        o.label.style.opacity = "1";
        o.marker.zIndex = 10;
        const avatarEl = o.frame.querySelector("[data-uploader-avatar]");
        if (avatarEl) avatarEl.style.opacity = "1";

        // Update photo based on photoIdx
        const photo = stops[i]?.photos?.[photoIdx];
        const activeSrc = photo?.blobUrl || photo?.url || stops[i]?.thumbnailUrl;
        if (activeSrc) {
          o.img.src = activeSrc;
          o.badge.textContent = `${photoIdx + 1}/${stops[i].photos.length}`;
          const dotEls = o.dots.querySelectorAll("span");
          dotEls.forEach((dot, j) => {
            dot.style.background = j === photoIdx ? "rgba(255,255,255,1)" : "rgba(255,255,255,0.5)";
            dot.style.width = j === photoIdx ? "12px" : "5px";
            dot.style.borderRadius = j === photoIdx ? "3px" : "50%";
          });
        }
      } else {
        o.frame.style.width = PHOTO_SMALL + "px";
        o.frame.style.height = PHOTO_SMALL + "px";
        o.frame.style.borderRadius = "6px";
        o.frame.style.borderWidth = "2px";
        o.frame.style.boxShadow = "0 2px 8px rgba(0,0,0,0.15)";
        o.badge.style.opacity = "0";
        o.dots.style.opacity = "0";
        o.label.style.opacity = "0";
        o.marker.zIndex = 1;
        const avatarEl = o.frame.querySelector("[data-uploader-avatar]");
        if (avatarEl) avatarEl.style.opacity = "0";

        // Reset to first photo
        const photo = stops[i]?.photos?.[0];
        const resetSrc = photo?.blobUrl || photo?.url || stops[i]?.thumbnailUrl;
        if (resetSrc) o.img.src = resetSrc;
      }
    });
  }, [currentIdx, arrived, photoIdx, isDesktop, stops, PHOTO_ACTIVE_W]);

  // Auto-play timer
  useEffect(() => {
    if (autoRef.current) clearInterval(autoRef.current);
    if (isPlaying && currentIdx >= 0 && !transitioning) {
      autoRef.current = setInterval(() => {
        const photos = stops[currentIdx]?.photos || [];
        if (photos.length > 1 && photoIdx < photos.length - 1) {
          setPhotoIdx((p) => p + 1);
        } else if (currentIdx < stops.length - 1) {
          if (goToStopRef.current) goToStopRef.current(currentIdx + 1);
        } else {
          setIsPlaying(false);
        }
      }, AUTO_INTERVAL);
    }
    return () => { if (autoRef.current) clearInterval(autoRef.current); };
  }, [isPlaying, currentIdx, transitioning, photoIdx]); // eslint-disable-line

  // Prezi-style fly
  const flyTo = useCallback((fromLat, fromLng, fromZoom, toLat, toLng, toZoom, duration = 1500) => {
    return new Promise((resolve) => {
      if (!map) { resolve(); return; }

      const dlat = Math.abs(toLat - fromLat);
      const dlng = Math.abs(toLng - fromLng);
      const dist = Math.sqrt(dlat * dlat + dlng * dlng);
      // Gentle zoom-out: less aggressive, proportional to distance
      let zoomDip;
      if (dist < 0.005) zoomDip = 0;
      else if (dist < 0.02) zoomDip = 0.6;
      else if (dist < 0.05) zoomDip = 1.2;
      else if (dist < 0.15) zoomDip = 2.0;
      else if (dist < 0.5) zoomDip = 3.0;
      else zoomDip = 4.0;
      const baseZoom = Math.min(fromZoom, toZoom);

      const start = performance.now();
      duration = Math.round(duration * 1.5);
      // Smooth ease — cubic in-out
      const ease = (t) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

      const animate = (now) => {
        const elapsed = now - start;
        const t = Math.min(elapsed / duration, 1);
        const et = ease(t);

        const lat = fromLat + (toLat - fromLat) * et;
        const lng = fromLng + (toLng - fromLng) * et;

        // Continuous sine curve for zoom — no hard midpoint split
        const zoomOffset = Math.sin(t * Math.PI) * zoomDip;
        const zoom = baseZoom + (toZoom - baseZoom) * et - zoomOffset;

        map.moveCamera({ center: { lat, lng }, zoom });

        if (t < 1) requestAnimationFrame(animate);
        else resolve();
      };
      requestAnimationFrame(animate);
    });
  }, [map]);

  // Calculate lat offset so photo center (not pin) is at screen center
  // AdvancedMarkerElement anchors at bottom-center, photo extends upward
  // Offset = half of (photo height + label height) in lat degrees
  const getPhotoOffset = useCallback((stopIdx) => {
    // Always use landscape ratio for consistent centering
    const h = Math.round(PHOTO_ACTIVE_W * 0.65);
    const labelH = 24;
    const totalH = h + labelH;
    // Convert pixels to lat degrees at ZOOM_CLOSE
    // At zoom z, 1 pixel ≈ 156543.03392 * cos(lat) / 2^z meters
    // 1 degree lat ≈ 111320 meters
    const metersPerPx = 156543.03392 / Math.pow(2, ZOOM_CLOSE);
    const offsetLat = (totalH / 2) * metersPerPx / 111320;
    return offsetLat;
  }, [PHOTO_ACTIVE_W, isDesktop]);

  // Navigate to a stop
  const goToStop = useCallback(async (idx) => {
    if (idx < 0 || idx >= stops.length || !map || transitioning) return;
    setTransitioning(true);
    setArrived(false);
    setPhotoIdx(0);

    const target = stops[idx];
    const prev = prevIdxRef.current >= 0 ? stops[prevIdxRef.current] : null;
    prevIdxRef.current = idx;
    const offset = getPhotoOffset(idx);

    if (!prev || currentIdx === -1) {
      const center = map.getCenter();
      const currentZoom = map.getZoom();
      await flyTo(center.lat(), center.lng(), currentZoom, target.lat + offset, target.lng, ZOOM_CLOSE, 1500);
      setCurrentIdx(idx);
      setTransitioning(false);
      setTimeout(() => setArrived(true), 100);
      return;
    }

    const prevOffset = getPhotoOffset(prevIdxRef.current >= 0 ? idx : 0);
    const dlat = Math.abs(target.lat - prev.lat);
    const dlng = Math.abs(target.lng - prev.lng);
    const dist = Math.sqrt(dlat * dlat + dlng * dlng);
    const duration = dist < 0.003 ? 1000 : dist < 0.03 ? 1500 : dist < 0.1 ? 2000 : 2500;

    await flyTo(prev.lat + prevOffset, prev.lng, ZOOM_CLOSE, target.lat + offset, target.lng, ZOOM_CLOSE, duration);

    setCurrentIdx(idx);
    setTransitioning(false);
    setTimeout(() => setArrived(true), 100);
  }, [stops, map, transitioning, currentIdx, isDesktop, flyTo, getPhotoOffset]);

  // Keep goToStopRef current
  useEffect(() => { goToStopRef.current = goToStop; }, [goToStop]);

  const handlePrev = () => {
    if (transitioning) return;
    const photos = stops[currentIdx]?.photos || [];
    if (photoIdx > 0) {
      setPhotoIdx(photoIdx - 1);
    } else if (currentIdx > 0) {
      goToStop(currentIdx - 1);
    }
  };

  const handleNext = () => {
    if (transitioning) return;
    const photos = stops[currentIdx]?.photos || [];
    if (photos.length > 1 && photoIdx < photos.length - 1) {
      setPhotoIdx(photoIdx + 1);
    } else if (currentIdx < stops.length - 1) {
      goToStop(currentIdx + 1);
    }
  };

  // Keep refs current for swipe handlers
  useEffect(() => { handlePrevRef.current = handlePrev; });
  useEffect(() => { handleNextRef.current = handleNext; });

  const handleClose = () => {
    setIsPlaying(false);
    if (autoRef.current) clearInterval(autoRef.current);
    // Clean up overlays
    overlaysRef.current.forEach((o) => { if (o.marker) o.marker.map = null; });
    overlaysRef.current = [];
    if (map && stops.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      stops.forEach((s) => bounds.extend({ lat: s.lat, lng: s.lng }));
      map.fitBounds(bounds, 60);
    }
    onClose();
  };

  const stop = currentIdx >= 0 ? stops[currentIdx] : null;
  const photos = stop?.photos || [];
  const progress = stops.length > 0 ? ((currentIdx + 1) / stops.length) * 100 : 0;
  const isFirst = currentIdx === 0 && photoIdx === 0;
  const isLast = currentIdx === stops.length - 1 && photoIdx === photos.length - 1;

  const barStyle = isDesktop
    ? { position: "absolute", bottom: 24, left: 24, width: 380 }
    : { position: "absolute", bottom: 24, left: 16, right: 16 };

  if (preloading) {
    return (
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0, left: leftOffset,
        zIndex: 60,
        background: "rgba(47,52,48,0.6)",
        display: "flex", alignItems: "center", justifyContent: "center",
        flexDirection: "column", gap: 12,
      }}>
        <div style={{
          width: 36, height: 36,
          border: "3px solid rgba(255,255,255,0.3)",
          borderTopColor: TRIP_COLOR,
          borderRadius: "50%",
          animation: "spin 0.8s linear infinite",
        }} />
        <p style={{ fontFamily: FL, fontSize: 13, fontWeight: 600, color: "#fff" }}>
          Loading map...
        </p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{
      position: "fixed", top: 0, right: 0, bottom: 0, left: leftOffset,
      zIndex: 60,
      pointerEvents: "none",
      opacity: fadeIn ? 1 : 0,
      transition: "opacity 0.5s",
    }}>
      {/* Vignette */}
      <div style={{
        position: "absolute", inset: 0,
        background: "radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.25) 100%)",
        pointerEvents: "none",
      }} />

      {/* Top bar */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0,
        padding: "12px 16px",
        background: "linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.15), transparent)",
        display: "flex", alignItems: "center", gap: 10,
        pointerEvents: "auto",
      }}>
        <button onClick={handleClose} style={{
          background: "rgba(255,255,255,0.2)", border: "none",
          borderRadius: 10, padding: "10px 12px",
          color: "#fff", cursor: "pointer",
          backdropFilter: "blur(8px)",
          display: "flex", alignItems: "center",
          flexShrink: 0,
        }}>
          <span className="material-symbols-outlined" style={{ fontSize: 22 }}>close</span>
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{
            margin: 0, fontFamily: FH, fontStyle: "italic",
            fontSize: 18, fontWeight: 700, color: "#fff",
            textShadow: "0 1px 6px rgba(0,0,0,0.4)",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>{trip.name}</p>
        </div>
        <button
          onClick={() => setIsPlaying(!isPlaying)}
          style={{
            background: isPlaying ? "rgba(255,255,255,0.3)" : "rgba(232,148,58,0.85)",
            border: "none",
            borderRadius: 10, padding: "10px 16px",
            color: "#fff", cursor: "pointer",
            backdropFilter: "blur(8px)",
            display: "flex", alignItems: "center", gap: 6,
            flexShrink: 0,
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 22 }}>
            {isPlaying ? "pause" : "play_arrow"}
          </span>
          <span style={{ fontFamily: FL, fontSize: 14, fontWeight: 700 }}>
            {isPlaying ? "Pause" : "Play"}
          </span>
        </button>
      </div>

      {/* Progress bar */}
      <div style={{
        position: "absolute", top: 58, left: 16, right: 16,
        height: 6, borderRadius: 3,
        background: "rgba(255,255,255,0.2)",
        pointerEvents: "none",
      }}>
        <div style={{
          height: "100%", borderRadius: 3,
          background: TRIP_COLOR,
          width: `${progress}%`,
          transition: "width 0.8s ease",
        }} />
        {stops.map((_, i) => (
          <div key={i} style={{
            position: "absolute",
            left: `${((i + 1) / stops.length) * 100}%`,
            top: -2, transform: "translateX(-50%)",
            width: i === currentIdx ? 8 : 5,
            height: i === currentIdx ? 8 : 5,
            borderRadius: "50%",
            background: i <= currentIdx ? TRIP_COLOR : "rgba(255,255,255,0.5)",
            border: i === currentIdx ? "2px solid #fff" : "none",
            transition: "all 0.3s",
          }} />
        ))}
      </div>

      {/* Bottom bar — compact, place info + nav */}
      {stop && arrived && (
        <div style={{
          ...barStyle,
          pointerEvents: "auto",
          opacity: arrived ? 1 : 0,
          transform: arrived ? "translateY(0)" : "translateY(20px)",
          transition: "opacity 0.4s ease, transform 0.4s ease",
          background: "rgba(255,255,255,0.95)",
          backdropFilter: "blur(12px)",
          borderRadius: 16,
          padding: "14px 18px",
          boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
          display: "flex", alignItems: "center", gap: 12,
        }}>
          <span style={{
            width: 32, height: 32, borderRadius: "50%",
            background: TRIP_COLOR, color: "#fff",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            fontFamily: FL, fontSize: 14, fontWeight: 800,
            flexShrink: 0,
          }}>{currentIdx + 1}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{
              margin: 0, fontFamily: FL, fontSize: 15, fontWeight: 700, color: "#2f3430",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>{stop.name || `Stop ${currentIdx + 1}`}</p>
            {stop.time && (
              <p style={{
                margin: "2px 0 0", fontFamily: FL, fontSize: 12, color: "#8a8e8a",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>{stop.time}</p>
            )}
            {stop.memo && (
              <p style={{
                margin: "3px 0 0", fontFamily: FH, fontStyle: "italic", fontSize: 12, color: "#5c605c",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>"{stop.memo}"</p>
            )}
          </div>
          <span style={{
            fontFamily: FL, fontSize: 10, color: "#8a8e8a",
            background: "#f4f4f0", padding: "3px 8px", borderRadius: 4,
            flexShrink: 0,
          }}>
            {currentIdx + 1} / {stops.length}
          </span>
          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
            <button onClick={handlePrev} disabled={isFirst} style={{
              width: 40, height: 40, borderRadius: 8,
              border: "none", cursor: isFirst ? "default" : "pointer",
              background: isFirst ? "#f4f4f0" : "#655d54",
              color: isFirst ? "#8a8e8a" : "#fff6ef",
              display: "flex", alignItems: "center", justifyContent: "center",
              opacity: isFirst ? 0.4 : 1,
            }}>
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>arrow_back</span>
            </button>
            {isLast && onSignIn ? (
              <button onClick={onSignIn} style={{
                height: 40, borderRadius: 8, padding: "0 14px",
                border: "none", cursor: "pointer",
                background: TRIP_COLOR, color: "#fff",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
                fontFamily: FL, fontSize: 12, fontWeight: 700,
                whiteSpace: "nowrap",
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>login</span>
                Sign in to save
              </button>
            ) : (
              <button onClick={handleNext} disabled={isLast} style={{
                width: 40, height: 40, borderRadius: 8,
                border: "none", cursor: isLast ? "default" : "pointer",
                background: isLast ? "#f4f4f0" : TRIP_COLOR,
                color: isLast ? "#8a8e8a" : "#fff",
                display: "flex", alignItems: "center", justifyContent: "center",
                opacity: isLast ? 0.4 : 1,
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>arrow_forward</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Transition label */}
      {transitioning && currentIdx >= 0 && (
        <div style={{
          position: "absolute",
          bottom: isDesktop ? 40 : 80,
          left: "50%", transform: "translateX(-50%)",
          background: "rgba(0,0,0,0.5)", color: "#fff",
          padding: "6px 14px", borderRadius: 8,
          fontFamily: FL, fontSize: 12, fontWeight: 600,
          backdropFilter: "blur(8px)",
          pointerEvents: "none",
          animation: "fadeInOut 1.8s ease",
        }}>
          {stops[currentIdx]?.name || `Stop ${currentIdx + 1}`}
        </div>
      )}

      <KeyboardHandler onPrev={handlePrev} onNext={handleNext} onClose={handleClose} onTogglePlay={() => setIsPlaying(!isPlaying)} />

      <style>{`
        @keyframes fadeInOut {
          0% { opacity: 0; transform: translateX(-50%) translateY(8px); }
          20% { opacity: 1; transform: translateX(-50%) translateY(0); }
          80% { opacity: 1; transform: translateX(-50%) translateY(0); }
          100% { opacity: 0; transform: translateX(-50%) translateY(-4px); }
        }
      `}</style>
    </div>
  );
}

function KeyboardHandler({ onPrev, onNext, onClose, onTogglePlay }) {
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "ArrowLeft") onPrev();
      else if (e.key === "ArrowRight" || e.key === " ") { e.preventDefault(); onNext(); }
      else if (e.key === "Escape") onClose();
      else if (e.key === "p") onTogglePlay();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onPrev, onNext, onClose, onTogglePlay]);
  return null;
}
